import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from '../components/Spinner';
import { useAuth } from '../contexts/AuthContext';
import { useUserContext } from '../hooks/useUserContext';
import { isValidDomain, normalizeDomain } from '../lib/domainUtils';
import { formatRelativeTime } from '../lib/timeFormat';
import {
  addMonitoredAsset,
  getAssetPrice,
  getMyAssets,
  removeMonitoredAsset,
  type MonitoredAssetRow,
  type NetworkDeviceRow,
} from '../server/api';

const TRIAL_DAYS = 30;

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.length === 3 ? currency.toUpperCase() : 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
}

function billingBadge(row: MonitoredAssetRow | NetworkDeviceRow): { label: string; className: string } {
  const st = row.billing_status;
  if (st === 'cancelled') {
    return { label: 'Cancelled', className: 'bg-foreground/10 text-muted' };
  }
  if (st === 'expired') {
    return { label: 'Trial expired', className: 'bg-danger/15 text-danger' };
  }
  if (st === 'trial' && row.is_trial && row.days_remaining !== null) {
    return {
      label: `Trial active · ${row.days_remaining} day${row.days_remaining === 1 ? '' : 's'} remaining`,
      className: 'bg-accent/15 text-accent',
    };
  }
  if (st === 'trial') {
    return { label: 'Trial active', className: 'bg-accent/15 text-accent' };
  }
  return { label: 'Active', className: 'bg-sky-500/15 text-sky-600 dark:text-sky-400' };
}

function priceDescription(
  row: MonitoredAssetRow,
  listPrice: { amount: number; currency: string } | null
): string {
  const rate = listPrice
    ? formatMoney(listPrice.amount, listPrice.currency)
    : formatMoney(row.monthly_amount, row.currency);
  if (row.billing_status === 'trial' && row.days_remaining !== null && row.days_remaining > 0) {
    return `Free for ${row.days_remaining} more day${row.days_remaining === 1 ? '' : 's'}, then ${rate}/mo`;
  }
  if (row.billing_status === 'trial') {
    return `Free for ${TRIAL_DAYS} days, then ${rate}/mo`;
  }
  return `${rate}/mo`;
}

function devicePriceLine(dev: NetworkDeviceRow): string {
  const rate = formatMoney(dev.monthly_amount, dev.currency);
  if (dev.billing_status === 'trial' && dev.days_remaining !== null && dev.days_remaining > 0) {
    return `Free for ${dev.days_remaining} more day${dev.days_remaining === 1 ? '' : 's'}, then ${rate}/mo`;
  }
  if (dev.billing_status === 'trial') {
    return `Free for ${TRIAL_DAYS} days, then ${rate}/mo`;
  }
  return `${rate}/mo`;
}

type SectionKey = 'email' | 'domain';

function sectionTrialBlockMessage(type: SectionKey): string {
  const label = type === 'email' ? 'email' : 'domain';
  return `You have 1 trial ${label} active. Upgrade to add more or wait for your trial to end.`;
}

export default function AssetsPage() {
  const { userProfile } = useAuth();
  const { display, countryCode, profileLoaded } = useUserContext();

  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'error' | 'ready'>('idle');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [monitored, setMonitored] = useState<MonitoredAssetRow[]>([]);
  const [devices, setDevices] = useState<NetworkDeviceRow[]>([]);

  const [prices, setPrices] = useState<{
    email: { amount: number; currency: string } | null;
    domain: { amount: number; currency: string } | null;
  }>({ email: null, domain: null });

  const [emailInput, setEmailInput] = useState('');
  const [domainInput, setDomainInput] = useState('');
  const [sectionError, setSectionError] = useState<{ email?: string; domain?: string }>({});
  const [adding, setAdding] = useState<{ email: boolean; domain: boolean }>({ email: false, domain: false });
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<MonitoredAssetRow | null>(null);

  const pricingCountry = useMemo(() => {
    if (countryCode && /^[A-Z]{2}$/i.test(countryCode)) return countryCode.toUpperCase();
    const fromProfile = userProfile?.country_code?.trim();
    if (fromProfile && /^[A-Z]{2}$/i.test(fromProfile)) return fromProfile.toUpperCase();
    return 'US';
  }, [countryCode, userProfile?.country_code]);

  const loadAssets = useCallback(async () => {
    setLoadState('loading');
    setLoadError(null);
    const res = await getMyAssets();
    if (!res.ok) {
      setLoadError(res.error);
      setLoadState('error');
      return;
    }
    setMonitored(res.monitored);
    setDevices(res.devices);
    setLoadState('ready');
  }, []);

  useEffect(() => {
    void loadAssets();
  }, [loadAssets]);

  useEffect(() => {
    if (!profileLoaded) return;
    let cancelled = false;
    void (async () => {
      const [e, d] = await Promise.all([
        getAssetPrice('email', pricingCountry, 'monthly'),
        getAssetPrice('domain', pricingCountry, 'monthly'),
      ]);
      if (cancelled) return;
      setPrices({
        email: e.ok ? e.price : null,
        domain: d.ok ? d.price : null,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [pricingCountry, profileLoaded]);

  const emails = useMemo(
    () => monitored.filter((a) => a.asset_type === 'email'),
    [monitored]
  );
  const domains = useMemo(
    () => monitored.filter((a) => a.asset_type === 'domain'),
    [monitored]
  );

  const emailSection = useMemo(() => {
    const activeRows = emails.filter((a) => a.billing_status !== 'cancelled');
    const trialBlock = activeRows.some((a) => a.billing_status === 'trial');
    const canAddMore = !(activeRows.length >= 1 && trialBlock);
    return { rows: emails, canAddMore, trialBlock };
  }, [emails]);

  const domainSection = useMemo(() => {
    const activeRows = domains.filter((a) => a.billing_status !== 'cancelled');
    const trialBlock = activeRows.some((a) => a.billing_status === 'trial');
    const canAddMore = !(activeRows.length >= 1 && trialBlock);
    return { rows: domains, canAddMore, trialBlock };
  }, [domains]);

  const activeEmailOrDomain = monitored.filter(
    (m) =>
      (m.asset_type === 'email' || m.asset_type === 'domain') && m.billing_status !== 'cancelled'
  );
  const fullyEmpty = activeEmailOrDomain.length === 0 && devices.length === 0;

  const subtitle = `Everything ShieldSME is monitoring for ${display.entityLabel}.`;

  const handleAdd = async (type: 'email' | 'domain') => {
    setSectionError((s) => ({ ...s, [type]: undefined }));
    const raw = type === 'email' ? emailInput.trim() : domainInput.trim();
    if (type === 'email') {
      if (!raw) {
        setSectionError((s) => ({ ...s, email: 'Enter an email address.' }));
        return;
      }
      if (!isValidEmail(raw)) {
        setSectionError((s) => ({ ...s, email: 'Enter a valid email address.' }));
        return;
      }
    } else {
      const norm = normalizeDomain(domainInput);
      if (!norm) {
        setSectionError((s) => ({ ...s, domain: 'Enter a domain.' }));
        return;
      }
      if (!isValidDomain(norm)) {
        setSectionError((s) => ({ ...s, domain: 'Enter a valid domain (e.g. yourbusiness.com).' }));
        return;
      }
    }

    setAdding((a) => ({ ...a, [type]: true }));
    const value = type === 'email' ? raw.toLowerCase() : normalizeDomain(domainInput)!;
    const result = await addMonitoredAsset(type, value);
    setAdding((a) => ({ ...a, [type]: false }));
    if (!result.ok) {
      setSectionError((s) => ({ ...s, [type]: result.error }));
      return;
    }
    if (type === 'email') setEmailInput('');
    else setDomainInput('');
    setMonitored((prev) => {
      const withoutDup = prev.filter((p) => p.id !== result.asset.id);
      return [result.asset, ...withoutDup];
    });
  };

  const openRemove = (row: MonitoredAssetRow) => {
    setPendingRemove(row);
  };

  const confirmRemove = async () => {
    if (!pendingRemove) return;
    const id = pendingRemove.id.trim();
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id)) {
      setSectionError((s) => ({ ...s, email: 'Invalid asset id' }));
      setPendingRemove(null);
      return;
    }
    setRemovingId(id);
    const res = await removeMonitoredAsset(id);
    setRemovingId(null);
    setPendingRemove(null);
    if (!res.ok) {
      setLoadError(res.error);
      return;
    }
    setMonitored((prev) =>
      prev.map((p) => (p.id === id ? { ...p, billing_status: 'cancelled' } : p))
    );
  };

  const listPriceForRow = (row: MonitoredAssetRow) =>
    row.asset_type === 'email' ? prices.email : prices.domain;

  return (
    <div className="mx-auto max-w-4xl space-y-10 pb-12">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
          My Protected Assets
        </h1>
        <p className="text-sm text-muted sm:text-base">{subtitle}</p>
      </header>

      {loadState === 'loading' || loadState === 'idle' ? (
        <div className="flex min-h-[30vh] items-center justify-center" aria-busy="true">
          <Spinner className="h-9 w-9 text-accent" />
        </div>
      ) : null}

      {loadState === 'error' ? (
        <div
          className="rounded-2xl border border-danger/40 bg-danger/10 p-6 text-sm text-danger"
          role="alert"
        >
          <p>{loadError ?? 'Could not load assets.'}</p>
          <button
            type="button"
            className="mt-4 rounded-lg border border-danger/50 bg-danger/10 px-4 py-2 text-sm font-semibold text-danger transition hover:bg-danger/20"
            onClick={() => void loadAssets()}
          >
            Retry
          </button>
        </div>
      ) : null}

      {loadState === 'ready' && fullyEmpty ? (
        <section
          className="rounded-2xl border border-border bg-surface/60 p-8 text-center"
          aria-labelledby="assets-empty-title"
        >
          <h2 id="assets-empty-title" className="text-lg font-semibold text-foreground">
            You haven&apos;t added anything to monitor yet.
          </h2>
          <p className="mt-2 text-sm text-muted">
            Start by adding your email or domain — your first one is free for 30 days.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              className="rounded-lg border border-accent/50 bg-accent/10 px-4 py-2.5 text-sm font-semibold text-accent transition hover:bg-accent/20"
              onClick={() => {
                document.getElementById('assets-email-section')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Add your email
            </button>
            <button
              type="button"
              className="rounded-lg border border-accent/50 bg-accent/10 px-4 py-2.5 text-sm font-semibold text-accent transition hover:bg-accent/20"
              onClick={() => {
                document.getElementById('assets-domain-section')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Add your domain
            </button>
          </div>
        </section>
      ) : null}

      {loadState === 'ready' ? (
        <>
          <AssetSection
            id="assets-email-section"
            sectionType="email"
            icon="📧"
            title="Email Addresses"
            rows={emailSection.rows}
            canAddMore={emailSection.canAddMore}
            trialBlock={emailSection.trialBlock}
            input={emailInput}
            onInputChange={setEmailInput}
            inputId="asset-add-email"
            inputLabel="Email address"
            inputType="email"
            placeholder="you@company.com"
            hintTrial="First email is free for 30 days"
            hintPrice={
              prices.email
                ? `Then ${formatMoney(prices.email.amount, prices.email.currency)}/mo based on your location`
                : 'Then billed monthly based on your location'
            }
            error={sectionError.email}
            adding={adding.email}
            onAdd={() => void handleAdd('email')}
            onRemove={openRemove}
            removingId={removingId}
            priceDescription={priceDescription}
            listPriceForRow={listPriceForRow}
          />

          <AssetSection
            id="assets-domain-section"
            sectionType="domain"
            icon="🌐"
            title="Domains"
            rows={domainSection.rows}
            canAddMore={domainSection.canAddMore}
            trialBlock={domainSection.trialBlock}
            input={domainInput}
            onInputChange={setDomainInput}
            inputId="asset-add-domain"
            inputLabel="Domain"
            inputType="text"
            placeholder="yourbusiness.com"
            hintTrial="First domain is free for 30 days"
            hintPrice={
              prices.domain
                ? `Then ${formatMoney(prices.domain.amount, prices.domain.currency)}/mo based on your location`
                : 'Then billed monthly based on your location'
            }
            error={sectionError.domain}
            adding={adding.domain}
            onAdd={() => void handleAdd('domain')}
            onRemove={openRemove}
            removingId={removingId}
            priceDescription={priceDescription}
            listPriceForRow={listPriceForRow}
          />

          <section className="space-y-4" aria-labelledby="devices-heading">
            <div className="flex items-center gap-2">
              <span className="text-2xl" aria-hidden>
                💻
              </span>
              <h2 id="devices-heading" className="text-lg font-semibold text-foreground">
                Devices
              </h2>
            </div>
            <p className="text-sm text-muted">
              Monitored devices use the ShieldSME security agent. Download the installer for your platform to add
              another device.
            </p>
            <Link
              to="/download"
              className="inline-flex w-fit rounded-lg border border-accent/40 bg-accent/10 px-4 py-2.5 text-sm font-semibold text-accent transition hover:bg-accent/20"
            >
              Download & install agent →
            </Link>
            {devices.length === 0 ? (
              <p className="text-sm text-muted">No enrolled devices yet.</p>
            ) : (
              <ul className="space-y-3">
                {devices.map((dev) => {
                  const badge = billingBadge(dev);
                  return (
                    <li
                      key={dev.id}
                      className="rounded-xl border border-border bg-surface/60 px-4 py-4 sm:flex sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-foreground">{dev.asset_value}</p>
                        <p className="mt-1 text-xs text-muted">
                          {dev.os_type ? `${dev.os_type} · ` : null}
                          Last seen{' '}
                          {dev.last_seen ? formatRelativeTime(dev.last_seen) : '—'} · Added{' '}
                          {formatRelativeTime(dev.created_at)}
                        </p>
                      </div>
                      <div className="mt-3 flex flex-col items-start gap-2 sm:mt-0 sm:items-end">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${badge.className}`}>
                          {badge.label}
                        </span>
                        <p className="text-xs text-muted">{devicePriceLine(dev)}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      ) : null}

      {pendingRemove ? (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="remove-asset-title"
          aria-describedby="remove-asset-desc"
        >
          <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-6 shadow-xl">
            <h2 id="remove-asset-title" className="text-lg font-semibold text-foreground">
              Remove monitored asset?
            </h2>
            <p id="remove-asset-desc" className="mt-3 text-sm text-muted">
              Stop monitoring <span className="font-medium text-foreground">{pendingRemove.asset_value}</span>? This
              marks the asset as cancelled in your billing history.
            </p>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                className="rounded-lg border border-border px-4 py-2.5 text-sm font-semibold text-foreground transition hover:bg-foreground/5"
                onClick={() => setPendingRemove(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={removingId !== null}
                className="rounded-lg bg-danger px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-danger/90 disabled:opacity-60"
                onClick={() => void confirmRemove()}
              >
                {removingId ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

type AssetSectionProps = {
  id: string;
  sectionType: SectionKey;
  icon: string;
  title: string;
  rows: MonitoredAssetRow[];
  canAddMore: boolean;
  trialBlock: boolean;
  input: string;
  onInputChange: (v: string) => void;
  inputId: string;
  inputLabel: string;
  inputType: 'email' | 'text';
  placeholder: string;
  hintTrial: string;
  hintPrice: string;
  error?: string;
  adding: boolean;
  onAdd: () => void;
  onRemove: (row: MonitoredAssetRow) => void;
  removingId: string | null;
  priceDescription: (row: MonitoredAssetRow, listPrice: { amount: number; currency: string } | null) => string;
  listPriceForRow: (row: MonitoredAssetRow) => { amount: number; currency: string } | null;
};

function AssetSection({
  id,
  sectionType,
  icon,
  title,
  rows,
  canAddMore,
  trialBlock,
  input,
  onInputChange,
  inputId,
  inputLabel,
  inputType,
  placeholder,
  hintTrial,
  hintPrice,
  error,
  adding,
  onAdd,
  onRemove,
  removingId,
  priceDescription,
  listPriceForRow,
}: AssetSectionProps) {
  return (
    <section id={id} className="space-y-4" aria-labelledby={`${id}-heading`}>
      <div className="flex items-center gap-2">
        <span className="text-2xl" aria-hidden>
          {icon}
        </span>
        <h2 id={`${id}-heading`} className="text-lg font-semibold text-foreground">
          {title}
        </h2>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-muted">None added yet.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => {
            const badge = billingBadge(row);
            const lp = listPriceForRow(row);
            return (
              <li
                key={row.id}
                className="rounded-xl border border-border bg-surface/60 px-4 py-4 sm:flex sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="font-medium text-foreground">{row.asset_value}</p>
                  <p className="mt-1 text-xs text-muted">Added {formatRelativeTime(row.created_at)}</p>
                </div>
                <div className="mt-3 flex flex-col items-start gap-2 sm:mt-0 sm:items-end">
                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${badge.className}`}>
                    {badge.label}
                  </span>
                  <p className="text-xs text-muted">{priceDescription(row, lp)}</p>
                  {row.billing_status !== 'cancelled' ? (
                    <button
                      type="button"
                      disabled={removingId === row.id}
                      onClick={() => onRemove(row)}
                      className="text-xs font-semibold text-danger underline-offset-2 hover:underline disabled:opacity-50"
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {trialBlock && !canAddMore ? (
        <p className="rounded-lg border border-border bg-foreground/5 px-3 py-2 text-sm text-muted">
          {sectionTrialBlockMessage(sectionType)}
        </p>
      ) : null}

      {canAddMore ? (
        <div className="rounded-xl border border-border bg-surface/40 p-4">
          <label htmlFor={inputId} className="block text-sm font-medium text-foreground">
            {inputLabel}
          </label>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-end">
            <input
              id={inputId}
              type={inputType}
              value={input}
              onChange={(e) => onInputChange(e.target.value)}
              placeholder={placeholder}
              className={`w-full min-w-0 flex-1 rounded-lg border border-border bg-navy/40 px-3 py-2.5 text-sm text-foreground outline-none ring-accent/30 focus:ring-2 ${
                error ? 'border-danger' : ''
              }`}
            />
            <button
              type="button"
              disabled={adding}
              onClick={onAdd}
              className="shrink-0 rounded-lg border border-accent/50 bg-accent/10 px-4 py-2.5 text-sm font-semibold text-accent transition hover:bg-accent/20 disabled:opacity-60"
            >
              {adding ? 'Adding…' : 'Add'}
            </button>
          </div>
          {error ? <p className="mt-2 text-sm text-danger">{error}</p> : null}
          <p className="mt-3 text-xs text-muted">{hintTrial}</p>
          <p className="mt-1 text-xs text-muted">{hintPrice}</p>
        </div>
      ) : null}
    </section>
  );
}
